/**
 * Page Status store — fetched once at app boot, kept fresh by the
 * `PageStatusBootstrapper` component. The router gate reads it
 * synchronously to decide what to render for the active route.
 *
 * Resolution rules:
 *  • Match the longest path prefix first.
 *  • Exact paths win over wildcards.
 *  • Wildcards use a trailing `/*` (e.g. `/zatoona/*` matches
 *    `/zatoona/anything/here`).
 *  • If nothing matches, the page is `live` by default.
 */

import { create } from 'zustand'
import type { PublicPageStatus, PageStatusKind } from './types'

interface PageStatusState {
  loaded: boolean
  /** Fetch error message, if any. UI ignores this and assumes 'live' on failure. */
  error: string | null
  list: PublicPageStatus[]
  setList: (list: PublicPageStatus[]) => void
  setError: (msg: string | null) => void
}

export const usePageStatusStore = create<PageStatusState>((set) => ({
  loaded: false,
  error: null,
  list: [],
  setList: (list) => set({ list, loaded: true, error: null }),
  setError: (error) => set({ error, loaded: true }),
}))

/**
 * Match a public path against the current page-status table.
 * Returns the most specific match, or null if no rule applies.
 */
export function resolvePageStatus(
  path: string,
  list: PublicPageStatus[],
): PublicPageStatus | null {
  // Normalise: drop trailing slash unless it's the root.
  const target = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path

  // Exact match wins.
  const exact = list.find((p) => p.path === target)
  if (exact) return exact

  // Wildcard prefix matches — pick longest.
  let bestMatch: PublicPageStatus | null = null
  for (const entry of list) {
    if (!entry.path.endsWith('/*')) continue
    const prefix = entry.path.slice(0, -2) // drop '/*'
    if (target === prefix || target.startsWith(`${prefix}/`)) {
      if (!bestMatch || prefix.length > bestMatch.path.length - 2) {
        bestMatch = entry
      }
    }
  }
  return bestMatch
}

/**
 * Helpful selector for the navbar — returns the set of paths that
 * should be hidden from any navigation surface (sidebar, footer, etc).
 */
export function getHiddenPaths(list: PublicPageStatus[]): Set<string> {
  const hidden = new Set<string>()
  for (const e of list) {
    if (e.status === 'hidden' || e.hideFromNav) {
      hidden.add(e.path)
    }
  }
  return hidden
}

/** Convenience kind tester. */
export function statusKindOf(
  path: string,
  list: PublicPageStatus[],
): PageStatusKind {
  return resolvePageStatus(path, list)?.status ?? 'live'
}
